import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const AnalyticsSidebar = ({ isOpen, onClose }) => {
  const popularDestinations = [
    { name: 'Ooty', bookings: 145, revenue: '₹2,18,000' },
    { name: 'Kodaikanal', bookings: 132, revenue: '₹1,98,000' },
    { name: 'Munnar', bookings: 128, revenue: '₹1,92,000' },
    { name: 'Coorg', bookings: 98, revenue: '₹1,47,000' },
    { name: 'Goa', bookings: 87, revenue: '₹1,30,500' }
  ];

  const bookingTrends = [
    { month: 'Jan', bookings: 65, revenue: 98000 },
    { month: 'Feb', bookings: 78, revenue: 117000 },
    { month: 'Mar', bookings: 92, revenue: 138000 },
    { month: 'Apr', bookings: 145, revenue: 217500 },
    { month: 'May', bookings: 156, revenue: 234000 },
    { month: 'Jun', bookings: 134, revenue: 201000 },
    { month: 'Jul', bookings: 167, revenue: 250500 }
  ];

  const packageCategories = [
    { name: 'Adventure', value: 35, color: '#C4622D' },
    { name: 'Cultural', value: 25, color: '#2C5F7A' },
    { name: 'Beach', value: 20, color: '#E8A547' },
    { name: 'Hill Station', value: 15, color: '#059669' },
    { name: 'Religious', value: 5, color: '#DC2626' }
  ];

  const recentMetrics = [
    { label: 'Total Packages', value: '248', change: '+12', trend: 'up' },
    { label: 'Active Bookings', value: '1,234', change: '+8.2%', trend: 'up' },
    { label: 'Monthly Revenue', value: '₹12,45,000', change: '+15.3%', trend: 'up' },
    { label: 'Avg. Rating', value: '4.6', change: '+0.2', trend: 'up' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-card border-l border-border shadow-prominent z-40 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Analytics Overview</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      <div className="p-6 space-y-8">
        {/* Key Metrics */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Key Metrics</h3>
          <div className="grid grid-cols-2 gap-4">
            {recentMetrics.map((metric, index) => (
              <div key={index} className="bg-muted/30 rounded-lg p-4">
                <div className="text-2xl font-bold text-foreground">{metric.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{metric.label}</div>
                <div className={`flex items-center space-x-1 mt-2 text-xs ${
                  metric.trend === 'up' ? 'text-success' : 'text-destructive'
                }`}>
                  <Icon name={metric.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={12} />
                  <span>{metric.change}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Booking Trends */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Booking Trends</h3>
          <div className="h-48 bg-muted/30 rounded-lg p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="bookings" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Popular Destinations */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Popular Destinations</h3>
          <div className="space-y-3">
            {popularDestinations.map((destination, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div>
                  <div className="font-medium text-foreground">{destination.name}</div>
                  <div className="text-xs text-muted-foreground">{destination.bookings} bookings</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-foreground">{destination.revenue}</div>
                  <div className="text-xs text-success">+{Math.floor(Math.random() * 20 + 5)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Package Categories */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Package Categories</h3>
          <div className="h-48 bg-muted/30 rounded-lg p-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={packageCategories}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {packageCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {packageCategories.map((category, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-foreground">{category.name}</span>
                </div>
                <span className="text-muted-foreground">{category.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue Chart */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Monthly Revenue</h3>
          <div className="h-48 bg-muted/30 rounded-lg p-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookingTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [`₹${(value / 1000).toFixed(0)}K`, 'Revenue']}
                />
                <Bar 
                  dataKey="revenue" 
                  fill="var(--color-secondary)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/30 rounded-lg transition-colors">
              <Icon name="Download" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Export Analytics Report</span>
            </button>
            <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/30 rounded-lg transition-colors">
              <Icon name="Settings" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Configure Metrics</span>
            </button>
            <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/30 rounded-lg transition-colors">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">Schedule Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSidebar;